import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Card, CardMedia, Typography, CardContent, CardActions, Grid } from '@material-ui/core';

import { useTranslation, useI18n } from '@common/utils';
import { useSettings } from '@common/hooks';
import { Button } from '@common/components';

const getBookingProductId = (booking, id) => booking?.products?.find(b => b.product === id)?._id;

function ColivingUpgrade({ booking, upgrade, handleAddUpgrade, handleRemoveUpgrade }) {
	const classes = useStyles();
	const tr = useTranslation();
	const i18n = useI18n(upgrade.i18n);

	const summary = i18n('summary', upgrade.summary);
	const title = i18n('title', upgrade.title);

	const { currencySymbol } = useSettings();

	const { image, price, upgradeType } = upgrade;
	const isRecurring = upgradeType === 'recurring';

	const handleAdd = () => handleAddUpgrade({ id: upgrade._id });
	const handleRemove = () => handleRemoveUpgrade({ id: getBookingProductId(booking, upgrade._id) });

	const totalInCart = booking?.products?.filter(b => b.product === upgrade._id && !b.mandatory)?.length;
	const isInCart = totalInCart > 0;

	return (
		<Card className={`${classes.card} ${upgrade.available === false ? classes.disabled : ''} ${isInCart && classes.upgradeInCart}`}>
			<div className={classes.area}>
				{!!image && <CardMedia className={classes.media} image={`/images/${image}`} title="" />}
				{!image && <div className={classes.media} />}
				<CardContent className={classes.content}>
					<Grid container>
						<Grid item xs={8} className={classes.header}>
							<Typography gutterBottom variant="h5" component="h5" className={classes.title}>
								{title}
							</Typography>
						</Grid>
						<Grid item xs={4} style={{ justifyContent: 'flex-end' }}>
							<div style={{ textAlign: 'right' }}>
								{price} {currencySymbol}/{isRecurring ? tr('mo') : tr('unit')}
							</div>
						</Grid>
						<Grid item xs={12}>
							<div dangerouslySetInnerHTML={{ __html: summary }} />
						</Grid>
					</Grid>
				</CardContent>
				<CardActions className={classes.actions}>
					{isRecurring
						? <Recurring
							handleAdd={handleAdd}
							handleRemove={handleRemove}
							isInCart={isInCart}
						/>
						: <Unit
							totalInCart={totalInCart}
							handleAdd={handleAdd}
							handleRemove={handleRemove}
						/>
					}
				</CardActions>
			</div>
		</Card>
	);
}

ColivingUpgrade.propTypes = {
	booking: PropTypes.object.isRequired,
	upgrade: PropTypes.object.isRequired,
	handleAddUpgrade: PropTypes.func.isRequired,
	handleRemoveUpgrade: PropTypes.func.isRequired,
};

const Recurring = ({ handleAdd, handleRemove, isInCart }) => {
	const classes = useStyles();
	const tr = useTranslation();

	const Add = () => (
		<Button
			color="blue"
			onClick={handleAdd}
			wide={true}
		>
			<span className={classes.qtyIncDec}>{tr('Add')}</span>
		</Button>
	);

	const Remove = () => (
		<Button
			color="red"
			wide={true}
			onClick={handleRemove}
		>
			<span className={classes.qtyIncDec}>{tr('Remove')}</span>
		</Button>
	);

	return (
		<Grid
			item
			container
			xs={12}
			style={{ justifyContent: 'center' }}
		>
			{isInCart ? <Remove /> : <Add />}
		</Grid>
	);
};
Recurring.propTypes = {
	handleAdd: PropTypes.func.isRequired,
	handleRemove: PropTypes.func.isRequired,
	isInCart: PropTypes.bool,
};

const Unit = ({ handleAdd, handleRemove, totalInCart }) => {
	const classes = useStyles();

	return (
		<Grid
			item
			container
			xs={12}
			style={{ justifyContent: 'space-between', alignItems: 'center' }}
		>
			<Button
				color="dark"
				third={true}
				onClick={handleRemove}
				disabled={!totalInCart || totalInCart < 1}
			>
				<span className={classes.qtyIncDec}>-</span>
			</Button>
			<div style={{ margin: '0 30px' }}>
				<Typography variant="h4" size={24}>
					{totalInCart}
				</Typography>
			</div>
			<Button
				color="dark"
				third={true}
				onClick={handleAdd}
			>
				<span className={classes.qtyIncDec}>+</span>
			</Button>
		</Grid>
	);
};
Unit.propTypes = {
	handleAdd: PropTypes.func.isRequired,
	handleRemove: PropTypes.func.isRequired,
	totalInCart: PropTypes.number,
};

export default ColivingUpgrade;

const useStyles = makeStyles(theme => ({
	title: {
		flex: '1 1 auto',
	},
	card: {
		borderRadius: '4px',
		border: `1px solid ${theme.palette.custom.borders}`,
		boxShadow: 'none',
		display: 'flex',
		flexDirection: 'column',
		flex: '1 1 auto',
		margin: '0 5px 10px 5px',
		backgroundColor: '#F7F7F7',
	},
	upgradeInCart: {
		borderColor: `${theme.palette.dark}`,
		border: '2.5px dashed',
	},
	area: {
		display: 'flex',
		flexDirection: 'column',
		flex: '1 1 auto',
	},
	header: {
		overflow: 'hidden',
	},

	content: {
		width: '100%',
		flex: '1 1 auto',

		[theme.breakpoints.down('sm')]: {
			padding: `${theme.spacing(2)}px`,
		},
	},
	media: {
		width: '100%',
		height: '248px',
		backgroundColor: '#EAEAEA',
	},
	disabled: {
		opacity: 0.4,
	},

	actions: {
		width: '100%',
		justifyContent: 'center',
	},

	qtyIncDec: {
		fontSize: 14,
	},
}));
